/**
 * Helpers used by NutriMart's checkout flow to build the payload sent to
 * the `send-transactional-email` edge function for the `order-confirmation`
 * template. Extracted from `NutriMart.tsx` so the composition logic can be
 * unit-tested without rendering the monolithic checkout component.
 *
 * The checkout flow doesn't capture a postal address yet, so the
 * `shippingAddress` field surfaces the chosen shipping method + ETA (or the
 * in-store pickup note) so the email matches what the user picked.
 */

export interface ShipOptLike {
  id: string;
  /** Localized display name, e.g. "Envío estándar". */
  nm: string;
  /** Localized ETA copy, e.g. "3-5 días" or "Hoy mismo". */
  tm: string;
  pr: number;
}

export interface CartItemLike {
  /** Either a plain string (already localized) or a per-language map. */
  nm: string | Record<string, string>;
  qty: number;
  /** Unit price in USD (caller converts to DOP). */
  pr: number;
}

export interface EmailItem {
  name: string;
  quantity: number;
  /** Formatted DOP string, e.g. "RD$ 1,234.56". */
  price: string;
}

export interface BuildEmailArgsInput {
  cart: CartItemLike[];
  lang: string;
  DOP: number;
  shipOpts: ShipOptLike[];
  shipId: string;
  cartTot: number;
  shipCost: number;
  total: number;
  orderId: string;
  dbOrderId: string;
  profile?: { email?: string | null; first_name?: string | null } | null;
  userEmail?: string | null;
}

export interface OrderConfirmationInvokeArgs {
  templateName: "order-confirmation";
  recipientEmail: string;
  idempotencyKey: string;
  templateData: {
    name?: string;
    orderId: string;
    items: EmailItem[];
    subtotal: string;
    shipping: string;
    total: string;
    shippingAddress?: string;
  };
}

/**
 * Build a human-readable shipping line from the selected shipping option.
 * - `pickup` uses an em-dash separator to read as a note ("Retiro — Hoy mismo")
 * - other methods wrap the ETA in parentheses ("Envío estándar (3-5 días)")
 * Returns `undefined` when no matching option exists so the template can
 * gracefully omit the section instead of rendering an empty box.
 */
export function composeShippingAddress(
  shipOpts: ShipOptLike[],
  shipId: string,
): string | undefined {
  const selected = shipOpts.find((o) => o.id === shipId);
  if (!selected) return undefined;
  if (selected.id === "pickup") return `${selected.nm} — ${selected.tm}`;
  return `${selected.nm} (${selected.tm})`;
}

/** Convert a USD amount to DOP at the given rate. */
export function usdToDop(usd: number, rate: number): number {
  return usd * rate;
}

/** Format a DOP amount as a human-readable string with thousands separators and the RD$ symbol. */
export function formatDop(amount: number): string {
  const parts = amount.toFixed(2).split(".");
  const integerPart = parts[0];
  const decimalPart = parts[1];
  const withCommas = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `RD$ ${withCommas}.${decimalPart}`;
}

/** Localize a cart item's name, falling back through the supported locales. */
export function localizeItemName(
  nm: CartItemLike["nm"],
  lang: string,
): string {
  if (typeof nm === "string") return nm;
  return nm[lang] || nm.en || nm.es || "";
}

/**
 * Build the full `supabase.functions.invoke('send-transactional-email', ...)`
 * payload for the order-confirmation email, OR return `null` when there's no
 * recipient email to deliver to (caller should skip the invoke in that case).
 */
export function buildOrderConfirmationInvokeArgs(
  input: BuildEmailArgsInput,
): OrderConfirmationInvokeArgs | null {
  const recipientEmail = input.profile?.email || input.userEmail || undefined;
  if (!recipientEmail) return null;

  const items: EmailItem[] = input.cart.map((c) => ({
    name: localizeItemName(c.nm, input.lang),
    quantity: c.qty,
    price: formatDop(usdToDop(c.pr, input.DOP)),
  }));

  const shippingAddress = composeShippingAddress(input.shipOpts, input.shipId);

  return {
    templateName: "order-confirmation",
    recipientEmail,
    idempotencyKey: `order-confirm-${input.dbOrderId}`,
    templateData: {
      name: input.profile?.first_name || undefined,
      orderId: input.orderId,
      items,
      subtotal: formatDop(usdToDop(input.cartTot, input.DOP)),
      shipping: formatDop(usdToDop(input.shipCost, input.DOP)),
      total: formatDop(usdToDop(input.total, input.DOP)),
      shippingAddress,
    },
  };
}